import { createElement, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import { useToggleButton } from 'react-aria';
import { useToggleState } from 'react-stately';

import defaultAriaProps from 'client/utils/constants/ariaPropTypes';

import iconButtonStyles from 'client/components/aria/Button/IconButton.module.scss';
import buttonStyles from 'client/components/aria/Button/Button.module.scss';

// https://react-spectrum.adobe.com/react-aria/useToggleButton.html
// https://react-spectrum.adobe.com/react-stately/useToggleState.html

// eslint-disable-next-line react/display-name
const Toggle = forwardRef(({ children, variant, className, elementType, icon, isDisabled, ...props }, ref) => {
    const defaultRef = useRef();
    const toggleRef = ref || defaultRef;

    const styles = icon ? iconButtonStyles : buttonStyles;

    const state = useToggleState(props);
    const { buttonProps } = useToggleButton({
        ...props,
        elementType: elementType,
        isDisabled
    }, state, ref);

    const { isSelected } = state;

    return createElement(elementType, {
        'data-testid': props['data-testid'],
        ref: toggleRef,
        className: [
            'govuk-button',
            styles[variant],
            className,
            isSelected ? styles.pressed : null,
            isDisabled ? styles.disabled : null
        ].join(' '),
        ...buttonProps,
        tabIndex: isDisabled ? -1 : 0
    }, children);
});

Toggle.propTypes = {
    ...defaultAriaProps,
    className: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'primary', 'secondary', 'error', 'warning', 'success', 'info', 'transparent']),
    elementType: PropTypes.oneOf(['button', 'span', 'div']),
    isDisabled: PropTypes.bool,
    icon: PropTypes.bool
};

Toggle.defaultProps = {
    variant: 'default',
    elementType: 'span'
};

export default Toggle;
